import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PlanCard from './components/PlanCard';
import FeatureComparisonTable from './components/FeatureComparisonTable';
import PaymentHistoryTable from './components/PaymentHistoryTable';
import CurrentSubscriptionWidget from './components/CurrentSubscriptionWidget';
import TrustSignals from './components/TrustSignals';
import PhonePeUPIOutageAlert from './components/PhonePeUPIOutageAlert';
import { uropayService } from '../../services/uropayService';

const SubscriptionPayment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('subscribe');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingPlanId, setStartingPlanId] = useState(null);

  const monthlyPriceDisplay = Number(uropayService.plans.monthly.amount).toLocaleString('en-IN');

  const premiumFeatures = [
    {
      title: 'Unlimited Messaging',
      description: 'Send unlimited messages to any user'
    },
    {
      title: 'Advanced Search Filters',
      description: 'Filter by interests, location, and more'
    },
    {
      title: 'Priority Profile Visibility',
      description: 'Your profile appears first in searches'
    },
    {
      title: 'Read Receipts',
      description: 'Know when your messages are read'
    },
    {
      title: 'Disappearing Messages',
      description: 'Send messages that auto-delete'
    },
    {
      title: 'Enhanced Privacy Controls',
      description: 'Advanced anonymity settings'
    },
    {
      title: 'Online Status Indicators',
      description: 'See who\'s online right now'
    },
    {
      title: 'Priority Support',
      description: '24-hour response time guaranteed'
    }
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const { data: subscription } = await uropayService.getActiveSubscription(user?.id);
        if (subscription) {
          setCurrentSubscription({
            isActive: subscription?.status === 'active' || subscription?.status === 'trialing',
            amount: subscription?.amount,
            renewalDate: subscription?.currentPeriodEnd,
            startDate: subscription?.currentPeriodStart,
            status: subscription?.status,
            stripeSubscriptionId: subscription?.stripeSubscriptionId,
            cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd,
            autoRenew: !subscription?.cancelAtPeriodEnd,
            planId: subscription?.planId || 'monthly',
            paymentMethod: 'UPI (UroPay)',
          });
        } else {
          setCurrentSubscription(null);
        }

        const { data: payments } = await uropayService.getPaymentHistory(user?.id);
        setPaymentHistory(payments || []);
      } catch (error) {
        console.error('Error loading subscription data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.id]);

  const handleSubscribe = async (planId) => {
    if (!user?.id) {
      alert('Please log in to subscribe');
      return;
    }
    setStartingPlanId(planId);
    try {
      const { data, error } = await uropayService.createPending(planId);
      if (error || !data?.buttonId || !data?.apiKey) {
        alert(error?.message || 'Failed to start UPI payment. Please try again.');
        return;
      }
      const selectedPlan = uropayService.plans[planId];
      navigate('/subscription-payment/uropay-embed', {
        state: {
          buttonId: data.buttonId,
          apiKey: data.apiKey,
          successRedirectUrl: data.successRedirectUrl,
          failureRedirectUrl: data.failureRedirectUrl,
          pendingId: data.pendingId,
          environment: data.environment || 'LIVE',
          amount: selectedPlan?.amount || 0,
          planId,
        },
      });
    } catch (error) {
      alert(error?.message || 'Something went wrong while starting UPI payment.');
    } finally {
      setStartingPlanId(null);
    }
  };

  const handleManageSubscription = () => {
    navigate('/subscription-payment');
  };

  const handleCancelSubscription = () => {
    alert('For cancellation support, please contact support.');
  };

  const tabs = [
    { id: 'subscribe', label: 'Subscribe', icon: 'CreditCard' },
    { id: 'current', label: 'Current Plan', icon: 'Crown' },
    { id: 'history', label: 'Payment History', icon: 'Receipt' }
  ];

  return (
    <>
      <Helmet>
        <title>Subscription & Payment - Bamchik</title>
        <meta name="description" content="Subscribe to Bamchik Premium for unlimited messaging, advanced features, and enhanced privacy controls" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1200px] px-4 lg:px-6 py-6 md:py-8 lg:py-12">
          <>
              <div className="text-center mb-6 md:mb-8 lg:mb-12">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-2 md:mb-3">
                  Unlock Premium Features
                </h1>
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
                  Get unlimited access to all features and connect with more people
                </p>
              </div>

              <div className="mb-6 md:mb-8 max-w-3xl mx-auto px-1">
                <PhonePeUPIOutageAlert />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 mb-6 md:mb-8 lg:mb-12">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-caption font-medium transition-smooth ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-warm'
                        : 'bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span className="text-sm md:text-base">{tab?.label}</span>
                  </button>
                ))}
              </div>

              {activeTab === 'subscribe' && (
                <div className="space-y-6 md:space-y-8 lg:space-y-12">
                  {currentSubscription?.isActive ? (
                    <div className="max-w-md mx-auto bg-card rounded-xl shadow-warm-lg p-6 md:p-8 text-center">
                      <div className="p-3 bg-success/10 rounded-full w-fit mx-auto mb-4">
                        <Icon name="Crown" size={32} color="var(--color-success)" />
                      </div>
                      <h2 className="text-xl md:text-2xl font-heading font-bold mb-2">You&apos;re on Premium</h2>
                      <p className="text-sm text-muted-foreground mb-4">
                        Your subscription is active{currentSubscription?.renewalDate ? ` until ${new Date(currentSubscription.renewalDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          variant="default"
                          iconName="Crown"
                          iconPosition="left"
                          onClick={() => setActiveTab('current')}
                        >
                          View current plan
                        </Button>
                        <Button
                          variant="outline"
                          iconName="CreditCard"
                          iconPosition="left"
                          onClick={handleManageSubscription}
                        >
                          Manage subscription
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      <PlanCard
                        title="Premium Monthly"
                        price={monthlyPriceDisplay}
                        period="month"
                        features={premiumFeatures}
                        isPopular={true}
                        onSubscribe={() => handleSubscribe('monthly')}
                        disabled={!!startingPlanId || currentSubscription?.isActive}
                        buttonText={startingPlanId === 'monthly' ? 'Starting UPI...' : 'Pay with UPI'}
                      />
                      <PlanCard
                        title="Premium Yearly"
                        price="5,000"
                        period="year"
                        features={premiumFeatures}
                        isPopular={false}
                        onSubscribe={() => handleSubscribe('yearly')}
                        disabled={!!startingPlanId || currentSubscription?.isActive}
                        buttonText={startingPlanId === 'yearly' ? 'Starting UPI...' : 'Pay with UPI'}
                      />
                      <PlanCard
                        title="Premium Lifetime"
                        price="10,000"
                        period="lifetime"
                        features={premiumFeatures}
                        isPopular={false}
                        onSubscribe={() => handleSubscribe('lifetime')}
                        disabled={!!startingPlanId || currentSubscription?.isActive}
                        buttonText={startingPlanId === 'lifetime' ? 'Starting UPI...' : 'Pay with UPI'}
                      />
                    </div>
                  )}

                  <TrustSignals />

                  <FeatureComparisonTable />

                  <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 md:p-8 lg:p-12 text-center">
                    <Icon name="HelpCircle" size={48} color="var(--color-primary)" className="mx-auto mb-4" />
                    <h2 className="text-xl md:text-2xl font-heading font-bold mb-2">Have Questions?</h2>
                    <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 max-w-2xl mx-auto">
                      Check our FAQ section or contact our support team for assistance
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        variant="outline"
                        iconName="MessageCircle"
                        iconPosition="left"
                        onClick={() => navigate('/faq')}
                      >
                        View FAQ
                      </Button>
                      <Button
                        variant="default"
                        iconName="Headphones"
                        iconPosition="left"
                      >
                        Contact Support
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'current' && (
                <div className="max-w-3xl mx-auto space-y-6">
                  {loading ? (
                    <div className="text-center py-12">
                      <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4 text-primary" />
                      <p className="text-muted-foreground">Loading subscription details...</p>
                    </div>
                  ) : currentSubscription?.isActive ? (
                    <CurrentSubscriptionWidget
                      subscription={currentSubscription}
                      onManage={handleManageSubscription}
                      onCancel={handleCancelSubscription}
                    />
                  ) : (
                    <div className="bg-card rounded-xl shadow-warm p-8 md:p-12 text-center">
                      <Icon name="Crown" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                      <h2 className="text-xl md:text-2xl font-heading font-bold mb-2">No Active Subscription</h2>
                      <p className="text-sm md:text-base text-muted-foreground mb-6">
                        Subscribe to Premium to unlock all features
                      </p>
                      <Button
                        onClick={() => setActiveTab('subscribe')}
                        iconName="Crown"
                        iconPosition="left"
                      >
                        View Plans
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="max-w-4xl mx-auto">
                  {loading ? (
                    <div className="text-center py-12">
                      <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4 text-primary" />
                      <p className="text-muted-foreground">Loading payment history...</p>
                    </div>
                  ) : (
                    <PaymentHistoryTable transactions={paymentHistory} />
                  )}
                </div>
              )}
          </>
        </main>
      </div>
    </>
  );
};

export default SubscriptionPayment;